<script>
import V4Button from 'dashboard/components-next/button/Button.vue';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SLAMetrics from './components/SLA/SLAMetrics.vue';
import SLATable from './components/SLA/SLATable.vue';
import SLAReportFilters from './components/SLA/SLAReportFilters.vue';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import ReportHeader from './components/ReportHeader.vue';
export default {
  name: 'SLAReports',
  components: {
    V4Button,
    ReportHeader,
    SLAMetrics,
    SLATable,
    SLAReportFilters,
  },
  data() {
    return {
      pageNumber: 1,
      isDownloading: false,
      viewActive: true,
      activeFilter: {
        from: 0,
        to: 0,
        assigned_agent_id: null,
        inbox_id: null,
        team_id: null,
        sla_policy_id: null,
        label_list: null,
      },
      loadError: '',
      metricsError: '',
      metricsRequestId: 0,
      metricsReady: false,
      metricsLoading: false,
      reportsReady: false,
      reportsLoading: false,
      loadRequestId: 0,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      slaReports: 'slaReports/getAll',
      slaMetrics: 'slaReports/getMetrics',
      slaMeta: 'slaReports/getMeta',
      uiFlags: 'slaReports/getUIFlags',
    }),
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('labels/get');
    this.$store.dispatch('sla/get');
  },
  beforeUnmount() {
    this.viewActive = false;
    this.loadRequestId += 1;
    this.metricsRequestId += 1;
  },
  methods: {
    async fetchSLAReports({ pageNumber } = {}) {
      this.loadRequestId += 1;
      const requestId = this.loadRequestId;
      const accountId = this.accountId;
      this.loadError = '';
      this.reportsReady = false;
      this.reportsLoading = true;
      try {
        await this.$store.dispatch('slaReports/get', {
          page: pageNumber || this.pageNumber,
          ...this.activeFilter,
        });
        if (requestId === this.loadRequestId && accountId === this.accountId) this.reportsReady = true;
      } catch {
        if (requestId !== this.loadRequestId || accountId !== this.accountId) return;
        this.loadError = this.$t('REPORT.DATA_FETCHING_FAILED');
        useAlert(this.loadError);
      } finally {
        if (requestId === this.loadRequestId && accountId === this.accountId) this.reportsLoading = false;
      }
    },
    async fetchSLAMetrics() {
      const requestId = ++this.metricsRequestId;
      const accountId = this.accountId;
      this.metricsReady = false;
      this.metricsLoading = true;
      this.metricsError = '';
      try {
        await this.$store.dispatch('slaReports/getMetrics', this.activeFilter);
        if (requestId === this.metricsRequestId && accountId === this.accountId) this.metricsReady = true;
      } catch {
        if (requestId !== this.metricsRequestId || accountId !== this.accountId) return;
        this.metricsError = this.$t('REPORT.DATA_FETCHING_FAILED');
        useAlert(this.metricsError);
      } finally {
        if (requestId === this.metricsRequestId && accountId === this.accountId) this.metricsLoading = false;
      }
    },
    onPageChange(pageNumber) {
      this.pageNumber = pageNumber;
      this.fetchSLAReports();
    },
    onFilterChange(params) {
      this.pageNumber = 1;
      this.activeFilter = params;
      this.fetchSLAReports();
      this.fetchSLAMetrics();
    },
    retryData() {
      this.fetchSLAReports();
      this.fetchSLAMetrics();
    },
    async downloadReports() {
      if (this.isDownloading) return;
      this.isDownloading = true;
      const accountId = this.accountId;
      const type = 'sla';
      try {
        await this.$store.dispatch('slaReports/download', {
          fileName: generateFileName({ type, to: this.activeFilter.to }),
          ...this.activeFilter,
        });
      } catch (error) {
        if (this.viewActive && this.accountId === accountId) {
          useAlert(this.$t('SLA_REPORTS.DOWNLOAD_FAILED'));
        }
      } finally {
        this.isDownloading = false;
      }
    },
  },
};
</script>

<template>
  <ReportHeader :header-title="$t('SLA_REPORTS.HEADER')">
    <V4Button
      :label="$t('SLA_REPORTS.DOWNLOAD_SLA_REPORTS')"
      icon="i-ph-download-simple"
      size="sm"
      :disabled="isDownloading"
      :is-loading="isDownloading"
      @click="downloadReports"
    />
  </ReportHeader>
  <div class="flex flex-col flex-1 gap-6">
    <SLAReportFilters
      :error-message="loadError || metricsError"
      @filter-change="onFilterChange"
      @retry="retryData"
    />
    <p v-if="metricsLoading" role="status" class="text-sm text-n-slate-11">{{ $t('SLA_REPORTS.LOADING_METRICS') }}</p>
    <SLAMetrics
      v-else-if="metricsReady"
      :hit-rate="slaMetrics.hitRate"
      :no-of-breaches="slaMetrics.numberOfSLAMisses"
      :no-of-conversations="slaMetrics.numberOfConversations"
      :is-loading="uiFlags.isFetchingMetrics"
    />
    <!-- Conta com SLA ligado mas nenhuma conversa no periodo mostrava so os
         tres indicadores e um vazio sem explicacao abaixo deles. -->
    <div
      v-if="!reportsLoading && !loadError && !slaReports.length"
      class="flex flex-col items-center gap-1 px-6 py-12 text-center"
      data-testid="sla-reports-empty"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('SLA_REPORTS.NO_RECORDS') }}
      </span>
      <span class="text-sm text-n-slate-11">
        {{ $t('SLA_REPORTS.EMPTY_BODY') }}
      </span>
    </div>
    <SLATable
      v-else-if="reportsReady || reportsLoading"
      :sla-reports="slaReports"
      :is-loading="reportsLoading"
      :current-page="Number(slaMeta.currentPage)"
      :total-count="Number(slaMeta.count)"
      @page-change="onPageChange"
    />
  </div>
</template>
